import { useParams, Link, useLocation } from 'wouter';
import { useGetUserProfile, getGetUserProfileQueryKey } from '@workspace/api-client-react';
import { Navbar } from '@/components/Navbar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Flame, User, ArrowLeft, Target, Calendar, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StreakBadges } from '@/components/StreakBadges';

export default function PublicProfile() {
  const params = useParams();
  const username = params.username!;
  const [, navigate] = useLocation();

  const { data: profile, isLoading } = useGetUserProfile(username, {
    query: { enabled: !!username, queryKey: getGetUserProfileQueryKey(username) },
  });

  return (
    <div className="min-h-[100dvh] bg-background">
      <Navbar />

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8 animate-slide-up">
        <Link href="/leaderboards">
          <Button variant="ghost" size="sm" className="mb-8 font-mono uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-none">
            <ArrowLeft className="mr-2 h-4 w-4" />
            BACK TO LEADERBOARDS
          </Button>
        </Link>

        {isLoading ? (
          <div className="space-y-8 animate-pulse">
            <Skeleton className="h-48 w-full rounded-none border border-primary/20 bg-muted/50" />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-32 w-full rounded-none border border-primary/20 bg-muted/50" />
              ))}
            </div>
          </div>
        ) : profile ? (
          <>
            <div className="mb-10 relative overflow-hidden bg-background border-2 border-secondary/50 p-6 sm:p-8 shadow-[0_0_30px_rgba(176,38,255,0.15)]">
              <div className="absolute inset-0 bg-[linear-gradient(hsl(280_100%_60%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(280_100%_60%_/_0.1)_1px,_transparent_1px)] bg-[size:20px_20px] opacity-20 pointer-events-none" />
              
              <div className="relative z-10 flex flex-col items-start gap-8 sm:flex-row sm:items-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-secondary/20 blur-xl rounded-none pointer-events-none" />
                  <Avatar className="h-28 w-28 border-4 border-secondary rounded-none shadow-[0_0_20px_rgba(176,38,255,0.4)]">
                    <AvatarImage src={profile.avatarUrl || undefined} alt={profile.username} className="object-cover" />
                    <AvatarFallback className="bg-background text-secondary font-display text-4xl rounded-none">
                      {profile.username[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div className="flex-1">
                  <div className="mb-4 flex flex-wrap items-center gap-4">
                    <h1 className="text-4xl sm:text-5xl font-black font-display uppercase tracking-widest text-shadow-neon-purple">
                      {profile.username}
                    </h1>
                    <div className="flex items-center gap-2 rounded-none bg-secondary/20 border border-secondary px-3 py-1 text-sm font-bold font-mono tracking-widest text-secondary glow-secondary shadow-[inset_0_0_10px_rgba(176,38,255,0.2)]">
                      LVL {profile.level}
                    </div>
                    <StreakBadges milestones={profile.streakBadges} size="md" />
                  </div>

                  <div className="flex flex-wrap gap-6 text-sm font-mono uppercase tracking-widest">
                    <div className="flex items-center gap-2">
                      <Flame className="h-5 w-5 text-secondary animate-pulse" />
                      <span className="font-bold text-secondary text-lg">{profile.currentStreak}</span>
                      <span className="text-muted-foreground">DAY STREAK</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-secondary" />
                      <span className="font-bold text-lg">{profile.gamesPlayed}</span>
                      <span className="text-muted-foreground">SESSIONS</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-secondary" />
                      <span className="font-bold text-lg">{Math.round((profile.accuracy ?? 0) * 100)}%</span>
                      <span className="text-muted-foreground">ACCURACY</span>
                    </div>
                    {profile.memberSince && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-secondary" />
                        <span className="text-muted-foreground">MEMBER SINCE</span>
                        <span className="font-bold text-lg">{new Date(profile.memberSince).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                  {profile.totalScore.toLocaleString()}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">TOTAL POINTS</div>
              </div>
              <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                  {profile.bestStreak}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">BEST STREAK</div>
              </div>
              <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                  {profile.gamesPlayed}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">MISSIONS</div>
              </div>
              <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4" data-testid="stat-avg-per-game">
                  {profile.gamesPlayed > 0 ? Math.round(profile.totalScore / profile.gamesPlayed).toLocaleString() : 0}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">AVG / GAME</div>
              </div>
              <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                  {profile.xp.toLocaleString()}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">TOTAL XP</div>
              </div>
              <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                  {profile.dailiesPlayed ?? 0}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">DAILY CONTESTS</div>
              </div>
              <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                  {Math.round((profile.accuracy ?? 0) * 100)}%
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">ACCURACY</div>
              </div>
              <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default" data-testid="stat-first-places">
                <div className="absolute top-0 left-0 w-2 h-full bg-[#FFD700]" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-[#FFD700] pl-4">
                  {profile.firstPlaces ?? 0}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">1ST PLACE RECORDS</div>
              </div>
              <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default" data-testid="stat-second-places">
                <div className="absolute top-0 left-0 w-2 h-full bg-gray-300" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-gray-300 pl-4">
                  {profile.secondPlaces ?? 0}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">2ND PLACE RECORDS</div>
              </div>
              <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default" data-testid="stat-third-places">
                <div className="absolute top-0 left-0 w-2 h-full bg-[#CD7F32]" />
                <div className="mb-2 text-4xl font-black font-display tracking-tighter text-[#CD7F32] pl-4">
                  {profile.thirdPlaces ?? 0}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">3RD PLACE RECORDS</div>
              </div>
            </div>

            {profile.gameBreakdown && profile.gameBreakdown.length > 0 && (
              <div className="mt-12">
                <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                  <Target className="h-8 w-8 text-secondary" />
                  GAME RECORD
                </h2>
                <div className="overflow-x-auto border border-card-border">
                  <table className="w-full text-left font-mono text-sm">
                    <thead>
                      <tr className="border-b border-card-border text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="p-4">Game</th>
                        <th className="p-4">Sport</th>
                        <th className="p-4 text-right">Plays</th>
                        <th className="p-4 text-right">Best Score</th>
                        <th className="p-4 text-right">Total Points</th>
                        <th className="p-4 text-right">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profile.gameBreakdown.map((g) => (
                        <tr
                          key={g.gameSlug}
                          className="border-b border-card-border/50 hover:bg-secondary/5 transition-colors cursor-pointer"
                          onClick={() => navigate(`/leaderboards?game=${encodeURIComponent(g.gameSlug)}`)}
                          title={`View ${g.gameTitle} all-time leaderboard`}
                          data-testid={`link-game-record-${g.gameSlug}`}
                        >
                          <td className="p-4 font-bold font-display uppercase tracking-wider">
                            <span className="inline-flex items-center gap-2">
                              {g.gameTitle}
                              {g.podiumRank != null && g.podiumRank <= 3 && (
                                <span title={`Ranked #${g.podiumRank} all-time`} data-testid={`podium-rank-${g.gameSlug}`}>
                                  <Trophy
                                    className={`h-4 w-4 ${g.podiumRank === 1 ? 'text-[#FFD700]' : g.podiumRank === 2 ? 'text-gray-300' : 'text-[#CD7F32]'}`}
                                  />
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="p-4 uppercase text-muted-foreground">{g.sport}</td>
                          <td className="p-4 text-right">{g.plays}</td>
                          <td className="p-4 text-right text-secondary font-bold">{g.bestScore.toLocaleString()}</td>
                          <td className="p-4 text-right">{g.totalScore.toLocaleString()}</td>
                          <td className="p-4 text-right">{Math.round(g.accuracy * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {profile.recentPlays && profile.recentPlays.length > 0 && (
              <div className="mt-12">
                <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                  <Zap className="h-8 w-8 text-primary" />
                  RECENT ACTIVITY
                </h2>
                <div className="space-y-4">
                  {profile.recentPlays.map((play, idx) => (
                    <div
                      key={idx}
                      className="group flex items-center justify-between border border-card-border bg-card p-4 hover:border-secondary/50 transition-colors"
                    >
                      <div>
                        <h3 className="mb-1 font-bold font-display uppercase tracking-widest text-lg group-hover:text-secondary transition-colors">{play.gameTitle}</h3>
                        <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                          &gt; {play.correctCount} / {play.totalRounds} HIT
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="mb-1 text-2xl font-black font-display tracking-tighter text-secondary">
                          {play.score}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                          {new Date(play.playedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-none border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center">
            <User className="mx-auto mb-6 h-16 w-16 text-primary animate-pulse opacity-50" />
            <p className="text-xl font-mono text-primary mb-2 uppercase tracking-widest">
              PLAYER NOT FOUND
            </p>
            <p className="mt-1 text-sm font-mono text-muted-foreground uppercase tracking-widest">
              THIS PLAYER MAY NOT EXIST
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
